import { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, FileText, Calendar, RefreshCw, Lock, ListChecks, Settings2 } from 'lucide-react';
import type { CierreDiaPreview, CierreDiaVista } from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourCierreDelDia } from '@renderer/tours';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { InsumoCierreCard, calcularEstadoCierre, type EstadoCierre } from '@renderer/components/cierre/InsumoCierreCard';
import { ModalCapturarFisico } from '@renderer/components/cierre/ModalCapturarFisico';
import { StepUpModal } from '@renderer/components/auth/StepUpModal';
import { useStepUp } from '@renderer/hooks/useStepUp';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 19 — Cierre del Día rediseñado.
 *
 * Estructura tipo pasos (flujo de checkout):
 *   [1] Revisa el día            (3 KPIs ya en cards)
 *   [2] Verifica insumos          (grid de cards con semáforo)
 *   [3] Decide opciones de cierre (reset stock + observaciones)
 *   [4] CERRAR + PDF
 *
 * El paso 2 es donde el rediseño cambia más: deja la tabla densa y muestra
 * un grid de cards por insumo, cada una con su estado real
 * (CUADRA / DIFERENCIA / DISCREPANCIA / SIN CAPTURAR / AGOTADO).
 */
// Sprint 24 — Prop `embebido` para reutilizar dentro del wizard "Cerrar Día"
// sin duplicar el header.
export function CierreDelDiaPage({
  embebido = false,
  onCambio,
}: { embebido?: boolean; onCambio?: () => void } = {}) {
  const sesion = useSessionStore((s) => s.sesion);
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const [preview, setPreview] = useState<CierreDiaPreview | null>(null);
  const [historial, setHistorial] = useState<CierreDiaVista[]>([]);
  const [resetStock, setResetStock] = useState(false);
  // Map<insumoId, fisico>. `null` o no presente = sin capturar.
  const [lecturas, setLecturas] = useState<Map<number, number>>(new Map());
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);
  const [modalCaptura, setModalCaptura] = useState<{
    insumoId: number; nombre: string; unidad: string; teorico: number;
  } | null>(null);
  // Sprint 32 — step-up de 2FA para cierre del día.
  const stepUp = useStepUp<CierreDiaVista>();

  const cargar = async () => {
    const [p, h] = await Promise.all([
      window.api.cierreDia.preview(),
      window.api.cierreDia.historial(15),
    ]);
    if (p.ok) setPreview(p.data);
    if (h.ok) setHistorial(h.data);
  };

  useEffect(() => {
    cargar();
  }, []);

  // Cuenta de insumos por estado, para mostrar resumen del paso 2.
  const conteoEstados = useMemo(() => {
    const c: Record<EstadoCierre, number> = {
      cuadra: 0, diferencia_menor: 0, discrepancia: 0, sin_capturar: 0, agotado: 0,
    };
    if (!preview) return c;
    for (const d of preview.detalleInsumos) {
      const fisico = lecturas.has(d.insumoId) ? lecturas.get(d.insumoId)! : null;
      c[calcularEstadoCierre(d.teorico, fisico)]++;
    }
    return c;
  }, [preview, lecturas]);

  const onEjecutar = async () => {
    if (!sesion) return;
    // Confirmación enriquecida si hay discrepancias mayores.
    const titulo = conteoEstados.discrepancia > 0
      ? `Tienes ${conteoEstados.discrepancia} insumo${conteoEstados.discrepancia === 1 ? '' : 's'} con discrepancia mayor`
      : '¿Cerrar el inventario?';
    const mensajeConfirm = [
      conteoEstados.discrepancia > 0
        ? `Hay discrepancias del 15% o más en algunos insumos. Al cerrar el inventario se registrarán como AJUSTE.`
        : null,
      resetStock ? 'Esto reiniciará el stock a 0.' : 'Esta acción no se puede deshacer. Solo se cierra el inventario — el día se marca como cerrado con el botón final del wizard.',
    ].filter(Boolean).join('\n\n');
    const ok = await window.confirmar({
      titulo,
      mensaje: mensajeConfirm,
      textoConfirmar: 'Sí, cerrar día',
      variant: conteoEstados.discrepancia > 0 ? 'warn' : 'info',
    });
    if (!ok) return;
    setEnviando(true);
    setMensaje(null);
    const lecturasFisicas = Array.from(lecturas.entries())
      .map(([insumoId, v]) => ({ insumoId, fisico: v }));
    // Sprint 32 — envuelto en stepUp.ejecutar: si la dueña tiene 2FA activo
    // y no hay step-up vigente, abre el modal y reintenta tras verificación.
    const res = await stepUp.ejecutar(() => window.api.cierreDia.ejecutar({
      usuarioId: sesion.usuarioId,
      resetStock,
      lecturasFisicas: lecturasFisicas.length > 0 ? lecturasFisicas : undefined,
    }));
    setEnviando(false);
    if (res.ok) {
      const pdf = await window.api.cierreDia.pdf(res.data.id);
      if (pdf.ok) await window.api.ticket.abrirPdf(pdf.data.filePath);
      setMensaje({ tipo: 'ok', texto: 'Cierre del día registrado.' });
      setLecturas(new Map());
      setResetStock(false);
      cargar();
      // Sprint 43 — notificar al wizard padre que este paso ya está hecho.
      onCambio?.();
    } else if (res.codigo === 'CANCELADO') {
      // El usuario canceló el step-up — no mostrar error.
      setMensaje(null);
    } else {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
  };

  const reimprimir = async (id: number) => {
    const res = await window.api.cierreDia.pdf(id);
    if (res.ok) await window.api.ticket.abrirPdf(res.data.filePath);
  };

  return (
    <div className="space-y-4">
      {!embebido && (
        <header className="flex items-center gap-3" data-tour="cierre-header">
          <ClipboardCheck className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Cierre del Día</h1>
            <p className="text-sm text-slate-500">Snapshot final con consumo de insumos vs ventas.</p>
          </div>
          <button onClick={cargar} className="ml-auto btn-ghost" title="Refrescar">
            <RefreshCw className="h-5 w-5" />
          </button>
          <HelpButton tour={tourCierreDelDia} />
        </header>
      )}

      {!preview ? (
        <div className="text-center text-slate-400 py-8">Cargando…</div>
      ) : (
        <>
          {/* [1] Revisa el día */}
          <div data-tour="cierre-paso-ventas">
          <PasoHeader numero={1} titulo="Revisa el día" />
          <div className="grid gap-4 md:grid-cols-3" data-tour="cierre-paso-ventas-grid">
            <Tarjeta titulo="Ventas del día" valor={formatPrecio(preview.ventasTotal, monedaSimbolo, locale)} />
            <Tarjeta titulo="Pedidos cobrados" valor={String(preview.pedidosCount)} />
            <Tarjeta titulo="Items vendidos" valor={String(preview.itemsCount)} />
          </div>
          </div>

          {/* [2] Verifica insumos */}
          <PasoHeader
            numero={2}
            titulo="Verifica insumos"
            subtitulo="Opcional: captura el conteo físico para que el sistema registre AJUSTE donde difiere del teórico."
          />
          {preview.detalleInsumos.length === 0 ? (
            <div className="card text-center py-6 text-slate-500">
              No hay insumos para revisar este día.
            </div>
          ) : (
            <>
              {/* Resumen rápido de estados */}
              <div className="card flex flex-wrap gap-2 items-center" data-tour="cierre-semaforo">
                <span className="text-xs uppercase tracking-wide text-slate-500 mr-2 flex items-center gap-1">
                  <ListChecks className="h-3.5 w-3.5" /> Resumen:
                </span>
                <EstadoChip etiqueta="🟢 Cuadran"      count={conteoEstados.cuadra} />
                <EstadoChip etiqueta="🟡 Diferencia"   count={conteoEstados.diferencia_menor} />
                <EstadoChip etiqueta="🔴 Discrepancia" count={conteoEstados.discrepancia} />
                <EstadoChip etiqueta="⚪ Sin capturar" count={conteoEstados.sin_capturar} />
                <EstadoChip etiqueta="⚫ Agotados"     count={conteoEstados.agotado} />
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {preview.detalleInsumos
                  .slice()
                  .sort((a, b) => {
                    // Discrepancias primero, después sin capturar, después diferencias, después cuadran, agotados al final.
                    const orden: Record<EstadoCierre, number> = {
                      discrepancia: 0, diferencia_menor: 1, sin_capturar: 2, cuadra: 3, agotado: 4,
                    };
                    const ea = calcularEstadoCierre(a.teorico, lecturas.has(a.insumoId) ? lecturas.get(a.insumoId)! : null);
                    const eb = calcularEstadoCierre(b.teorico, lecturas.has(b.insumoId) ? lecturas.get(b.insumoId)! : null);
                    if (orden[ea] !== orden[eb]) return orden[ea] - orden[eb];
                    return a.nombre.localeCompare(b.nombre);
                  })
                  .map((d) => (
                    <InsumoCierreCard
                      key={d.insumoId}
                      detalle={d}
                      fisico={lecturas.has(d.insumoId) ? lecturas.get(d.insumoId)! : null}
                      onCapturar={() =>
                        setModalCaptura({
                          insumoId: d.insumoId,
                          nombre: d.nombre,
                          unidad: d.unidad,
                          teorico: d.teorico,
                        })
                      }
                    />
                  ))}
              </div>
            </>
          )}

          {/* [3] Decide opciones */}
          <PasoHeader numero={3} titulo="Decide opciones del cierre" />
          <div className="card bg-amber-50 border-amber-200 space-y-3" data-tour="cierre-opciones">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={resetStock}
                onChange={(e) => setResetStock(e.target.checked)}
                className="h-5 w-5 mt-0.5"
              />
              <div>
                <div className="font-medium">Reset de stock al cerrar (lleva todo a 0)</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Marca esto si al final del día desechas o reservas todo el inventario perecedero.
                  Se registrarán movimientos de tipo <strong>CIERRE</strong>.
                </div>
              </div>
            </label>
          </div>

          {/* [4] Cerrar */}
          <PasoHeader numero={4} titulo="Cerrar el inventario" />
          <div className="card" data-tour="cierre-boton-cerrar">
            <button onClick={onEjecutar} disabled={enviando} className="btn-danger btn-lg w-full">
              <Lock className="h-5 w-5" />
              {enviando ? 'Cerrando inventario…' : 'Cerrar inventario y generar PDF'}
            </button>
            {mensaje && (
              <div
                className={`mt-3 rounded-lg px-3 py-2 text-sm ${
                  mensaje.tipo === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}
                role="alert"
              >
                {mensaje.texto}
              </div>
            )}
          </div>

          {/* Historial — sin cambios visuales mayores */}
          <div className="card">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Calendar className="h-5 w-5" /> Historial de cierres
            </h3>
            {historial.length === 0 ? (
              <p className="text-sm text-slate-500">Sin cierres registrados aún.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2">Fecha</th>
                      <th className="text-right px-3 py-2">Ventas</th>
                      <th className="text-right px-3 py-2">Merma total</th>
                      <th className="text-center px-3 py-2">Reset</th>
                      <th className="text-center px-3 py-2">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-3 py-2">{c.fecha.slice(0, 10)}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          {formatPrecio(c.totalVentas, monedaSimbolo, locale)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{c.totalMerma.toFixed(3)}</td>
                        <td className="px-3 py-2 text-center">{c.resetStock ? '✓' : ''}</td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => reimprimir(c.id)} className="btn-ghost" title="Abrir PDF">
                            <FileText className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {modalCaptura && (
        <ModalCapturarFisico
          nombre={modalCaptura.nombre}
          unidad={modalCaptura.unidad}
          teorico={modalCaptura.teorico}
          valorInicial={lecturas.has(modalCaptura.insumoId) ? lecturas.get(modalCaptura.insumoId)! : null}
          onClose={() => setModalCaptura(null)}
          onGuardar={(v) => {
            const next = new Map(lecturas);
            if (v === null) next.delete(modalCaptura.insumoId);
            else next.set(modalCaptura.insumoId, v);
            setLecturas(next);
            setModalCaptura(null);
          }}
        />
      )}

      {/* Sprint 32 — Modal de step-up 2FA. Aparece sólo cuando el handler
          retorna STEP_UP_REQUIRED. */}
      <StepUpModal
        abierto={stepUp.modalAbierto}
        onConfirmado={stepUp.onConfirmado}
        onCancelar={stepUp.onCancelar}
      />
    </div>
  );
}

function PasoHeader({ numero, titulo, subtitulo }: { numero: number; titulo: string; subtitulo?: string }) {
  return (
    <div className="flex items-start gap-2 mt-2">
      <span className="inline-flex items-center justify-center rounded-full bg-primary text-white text-sm font-bold w-7 h-7 shrink-0">
        {numero}
      </span>
      <div className="flex-1">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          {numero === 3 && <Settings2 className="h-4 w-4 text-slate-400" />}
          {titulo}
        </h2>
        {subtitulo && <p className="text-xs text-slate-500 mt-0.5">{subtitulo}</p>}
      </div>
    </div>
  );
}

function EstadoChip({ etiqueta, count }: { etiqueta: string; count: number }) {
  if (count === 0) return null;
  return (
    <span className="rounded-full bg-white border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">
      {etiqueta} <span className="font-bold ml-0.5">{count}</span>
    </span>
  );
}

function Tarjeta({ titulo, valor }: { titulo: string; valor: string }) {
  return (
    <div className="card text-center">
      <p className="text-xs text-slate-500 uppercase">{titulo}</p>
      <p className="text-3xl font-bold font-mono mt-1">{valor}</p>
    </div>
  );
}
